import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ClockIcon, FileSearch, BookOpen, CheckIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
import { trackPixel } from '../lib/metaPixel';
import { OFFER_URL } from '../lib/clickMagick';
import { questions, loadingSteps, researchInsights } from '../data/quizContent';
type Step = 'landing' | 'quiz' | 'loading' | 'results';
export function QuizFlow() {
  const screenInit = useScreenInit() as {
    step?: Step;
    currentQuestionIndex?: number;
  };
  const [step, setStep] = useState<Step>(screenInit.step ?? 'landing');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const idx = screenInit.currentQuestionIndex ?? 0;
    return Math.min(Math.max(idx, 0), questions.length - 1);
  });
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [completedLoadingSteps, setCompletedLoadingSteps] = useState(0);
  const safeIndex = Math.min(Math.max(currentQuestionIndex, 0), questions.length - 1);
  const currentQuestion = questions[safeIndex];
  const progress = step === 'landing' ? 0 : step === 'quiz' ? safeIndex / questions.length * 100 : step === 'loading' ? 92 : 100;
  const handleStart = () => {
    trackPixel('Lead', {
      content_name: 'Joint Quiz Started'
    });
    setStep('quiz');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleAnswer = (answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [safeIndex]: answer
    }));
    if (safeIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => Math.min(prev + 1, questions.length - 1));
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 260);
    } else {
      trackPixel('CompleteRegistration', {
        content_name: 'Joint Quiz Completed'
      });
      setTimeout(() => {
        setStep('loading');
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 260);
    }
  };
  useEffect(() => {
    if (step !== 'loading') return;
    setCompletedLoadingSteps(0);
    const timers: number[] = [];
    loadingSteps.forEach((_, idx) => {
      timers.push(window.setTimeout(() => setCompletedLoadingSteps(idx + 1), 900 * (idx + 1)));
    });
    timers.push(window.setTimeout(() => setStep('results'), 900 * loadingSteps.length + 700));
    return () => timers.forEach(t => window.clearTimeout(t));
  }, [step]);
  const handleWatchPresentation = () => {
    trackPixel('ViewContent', {
      content_name: 'VSL Presentation'
    });
    window.location.href = OFFER_URL;
  };
  return <div className="min-h-screen flex flex-col items-center w-full" data-id="element-2" data-mp="10llk12rwxbf">
      <header className="w-full bg-white border-b border-[#E5E2DC] sticky top-0 z-50" data-id="element-3" data-mp="3j9m4brwjt8oc">
        <div className="max-w-3xl mx-auto px-4 py-4 flex flex-col items-center justify-center" data-id="element-4" data-mp="3o6cjmd0j408p">
          <p className="text-2xl md:text-3xl font-bold text-[#2C3E35] tracking-tight font-serif" data-id="element-5" data-mp="34ksw8utgd8bc">
            Dr. Weis{' '}
            <span className="font-light italic text-[#7C9082]" data-id="element-6" data-mp="2zo2fs6dqkape">
              Joint Research
            </span>
          </p>
        </div>
        <div className="w-full bg-[#F4F1EA] h-1.5" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100} aria-label="Quiz progress" data-id="element-7" data-mp="39koximarmln7">
          <motion.div className="h-full bg-[#7C9082]" initial={{
          width: 0
        }} animate={{
          width: `${progress}%`
        }} transition={{
          duration: 0.5,
          ease: 'easeInOut'
        }} data-id="element-8" data-mp="3ikc3llefwfml" />
        </div>
      </header>

      <main className="w-full max-w-2xl mx-auto px-4 mt-8 md:mt-12 flex-1" data-id="element-9" data-mp="3sdj5uxdvvfig">
        <AnimatePresence mode="wait" data-id="element-10" data-mp="a5tmyvafnrrd">
          {step === 'landing' && <motion.section key="landing" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="text-center" data-id="element-11" data-mp="659pfqly6m4p">
              <p className="inline-block bg-[#F4F1EA] text-[#5A6D60] px-4 py-1.5 rounded-full text-sm font-medium tracking-wide" data-id="element-12" data-mp="24jfpjjia7d75">
                Joint Health Research · Ages 40+
              </p>

              <h1 className="mt-8 text-4xl md:text-5xl font-bold text-[#2C3E35] leading-tight" data-id="element-13" data-mp="2edeo68j5ceuc">
                A Harvard-Led Study Questioned What We Thought We Knew About{' '}
                <span className="text-[#7C9082]" data-id="element-14" data-mp="1ln68qofzw8jg">Aging Joints</span>
              </h1>

              <p className="mt-8 text-lg md:text-xl text-[#4A5D50] leading-relaxed max-w-xl mx-auto" data-id="element-15" data-mp="1qkfzq8j18s9e">
                Researchers examined thousands of ancient skeletons and found far
                less joint decay than expected. Answer 4 quick questions to see
                what current research points to instead — and why attention has
                turned to the gel-like fluid inside the joint.
              </p>

              <div className="mt-10" data-id="element-16" data-mp="uh8upqtuemj9">
                <button type="button" onClick={handleStart} className="w-full md:w-auto bg-[#7C9082] hover:bg-[#6A7E70] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#7C9082]/40 text-white text-lg font-medium py-4 px-12 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center mx-auto group" data-id="element-17" data-mp="qdkk0z9wzats">
                  Start the 30-Second Quiz
                  <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-18" data-mp="6ry2ixqk60gq" />
                </button>

                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-2 text-sm text-[#8A9A8F]" data-id="element-19" data-mp="3tut1hi0qc8nz">
                  <span className="flex items-center gap-2" data-id="element-20" data-mp="1x0y90tbzmy4o">
                    <ClockIcon className="w-4 h-4" aria-hidden="true" data-id="element-21" data-mp="1s3fxqwai06ti" /> 4
                    questions · about 30 seconds
                  </span>
                  <span className="flex items-center gap-2" data-id="element-22" data-mp="2b68c0dv35q0b">
                    <BookOpen className="w-4 h-4" aria-hidden="true" data-id="element-23" data-mp="2fz1hx52aj4dz" /> Based on
                    published research
                  </span>
                </div>
              </div>
            </motion.section>}

          {step === 'quiz' && <motion.section key="quiz" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="w-full" aria-labelledby="question-heading" data-id="element-24" data-mp="2743l3mw0v7hd">
              <div className="mb-8 text-center" data-id="element-25" data-mp="2kx31rs7jj7qi">
                <p className="text-sm font-medium text-[#7C9082] tracking-widest uppercase" data-id="element-26" data-mp="2pwfnuaql8as1">
                  Question {safeIndex + 1} of {questions.length}
                </p>
                <h2 id="question-heading" className="text-2xl md:text-3xl font-semibold text-[#2C3E35] mt-4 leading-snug" data-id="element-27" data-mp="19ksbptk6lppr">
                  {currentQuestion.question}
                </h2>
                {currentQuestion.helper && <p className="mt-3 text-[#7A8A80]" data-id="element-28" data-mp="12112yutpuwok">{currentQuestion.helper}</p>}
              </div>

              <div className="space-y-3" data-id="element-29" data-mp="1azlb521nhnyj">
                {currentQuestion.options.map(option => <button key={option} type="button" onClick={() => handleAnswer(option)} className="w-full text-left p-5 rounded-xl border-2 border-[#E5E2DC] bg-white hover:border-[#7C9082] hover:bg-[#F4F1EA] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#7C9082]/30 transition-all text-[#2C3E35] text-lg font-medium shadow-sm hover:shadow-md flex items-center justify-between group" data-id="element-30" data-mp="n9r0mr4t0bhg">
                    <span data-id="element-31" data-mp="3vq3nrqjc4ox8">{option}</span>
                    <span className="w-6 h-6 shrink-0 ml-4 rounded-full border-2 border-[#D1CEC7] group-hover:border-[#7C9082] flex items-center justify-center" aria-hidden="true" data-id="element-32" data-mp="3vp2e9qhywahm">
                      <span className="w-3 h-3 rounded-full bg-[#7C9082] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-33" data-mp="3k4ool28anwdf" />
                    </span>
                  </button>)}
              </div>
            </motion.section>}

          {step === 'loading' && <motion.section key="loading" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col items-center justify-center py-16 text-center" aria-live="polite" data-id="element-34" data-mp="3u11q2ma2zeiq">
              <div className="relative w-20 h-20" data-id="element-35" data-mp="gnyg8gp8p78k">
                <span className="absolute inset-0 border-4 border-[#E5E2DC] rounded-full" data-id="element-36" data-mp="gp9fc37qpqtc" />
                <motion.span className="absolute inset-0 border-4 border-[#7C9082] rounded-full border-t-transparent" animate={{
              rotate: 360
            }} transition={{
              duration: 1,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-37" data-mp="lnujgdq44un2" />
                <span className="absolute inset-0 flex items-center justify-center text-[#7C9082]" data-id="element-38" data-mp="1w64em72snge8">
                  <FileSearch className="w-7 h-7" aria-hidden="true" data-id="element-39" data-mp="1r8mg7amekjkh" />
                </span>
              </div>

              <h2 className="mt-8 text-2xl font-serif text-[#2C3E35]" data-id="element-40" data-mp="2oujsgqa6vsnz">
                Putting your summary together
              </h2>

              <ul className="mt-8 space-y-3 text-left" data-id="element-41" data-mp="2jyl7zdrzeusu">
                {loadingSteps.map((label, idx) => {
              const done = idx < completedLoadingSteps;
              return <li key={label} className="flex items-center gap-3" data-id="element-42" data-mp="2a4m8ynn7fr54" data-mpi={idx}>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center border-2 transition-colors ${done ? 'bg-[#7C9082] border-[#7C9082]' : 'border-[#D1CEC7]'}`} aria-hidden="true" data-id="element-43" data-mp="37munjxylqmbj">
                        {done && <CheckIcon className="w-3 h-3 text-white" data-id="element-44" data-mp="uq566zm86077" />}
                      </span>
                      <span className={done ? 'text-[#2C3E35] font-medium' : 'text-[#8A9A8F]'} data-id="element-45" data-mp="pphionx7g1hq">
                        {label}
                      </span>
                    </li>;
            })}
              </ul>
            </motion.section>}

          {step === 'results' && <motion.section key="results" initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-white rounded-2xl shadow-xl p-7 md:p-10 border border-[#E5E2DC]" data-id="element-46" data-mp="18haaif0jfgnv">
              <p className="text-sm font-medium text-[#7C9082] tracking-widest uppercase text-center" data-id="element-47" data-mp="220gjf2jf4ye6">
                Your research summary
              </p>

              <h2 className="mt-4 text-3xl md:text-4xl font-bold text-[#2C3E35] text-center leading-tight" data-id="element-48" data-mp="25zz540j2ddi2">
                Researchers are looking at aging joints differently now
              </h2>

              <p className="mt-6 text-lg text-[#4A5D50] leading-relaxed" data-id="element-49" data-mp="1x3gcyqinptmj">
                For decades, joint discomfort after 40 was explained as simple
                "wear and tear." The Harvard-led skeleton research challenged
                that idea — and pointed attention somewhere else entirely.
              </p>

              <div className="mt-8 space-y-5" data-id="element-50" data-mp="1eavwy9o23j05">
                {researchInsights.map(insight => <article key={insight.heading} className="bg-[#F9F8F6] p-6 rounded-xl border-l-4 border-[#7C9082]" data-id="element-51" data-mp="zdd1ypksc9k9">
                    <h3 className="text-lg font-semibold text-[#2C3E35] font-serif" data-id="element-52" data-mp="l8coysmqfoub">
                      {insight.heading}
                    </h3>
                    <p className="mt-2 text-[#4A5D50] leading-relaxed" data-id="element-53" data-mp="gguos6sj9sae">
                      {insight.body}
                    </p>
                  </article>)}
              </div>

              <div className="mt-8 bg-[#F4F1EA] p-6 rounded-xl" data-id="element-54" data-mp="6krn121lfvnz">
                <p className="text-[#2C3E35] leading-relaxed" data-id="element-55" data-mp="146xh8o0d109j">
                  In his free presentation, Dr. Mark Weis walks through this
                  research in detail — including what he observed about the
                  hyaluronan-supporting diet in the village of Yuzurihara, Japan,
                  and the simple 10-second daily habit he describes.
                </p>
              </div>

              <div className="mt-9" data-id="element-56" data-mp="3ezrw0dp56dlu">
                <button type="button" onClick={handleWatchPresentation} className="w-full bg-[#C2754E] hover:bg-[#A9623F] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#C2754E]/40 text-white text-lg md:text-xl font-bold py-5 px-6 rounded-xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-0.5 flex flex-col items-center justify-center gap-1.5" data-id="element-57" data-mp="3f0thlc3saiht">
                  <span data-id="element-58" data-mp="247j05ao6e1y0">Watch Dr. Weis's Free Video Presentation</span>
                  <span className="text-sm font-normal opacity-90" data-id="element-59" data-mp="248u53dtcxfqp">
                    Presented by Dr. Mark Weis · Sound required
                  </span>
                </button>

                <p className="mt-5 text-center text-sm text-[#8A9A8F]" data-id="element-60" data-mp="2d5w3v8dys1ky">
                  This summary is general educational information based on
                  published research. It is not a diagnosis, medical advice, or a
                  treatment recommendation.
                </p>
              </div>
            </motion.section>}
        </AnimatePresence>
      </main>

      <footer className="w-full max-w-2xl mx-auto px-4 mt-14 pb-10" data-id="element-61" data-mp="ssdtxwou5l9n">
        <p className="text-xs text-[#9AA79E] leading-relaxed text-center" data-id="element-62" data-mp="2j86dg0j9u61t">
          The content on this page is for general information and education only
          and is not intended as medical advice, diagnosis, or treatment. Studies
          referenced describe research findings and do not describe results you
          should expect. Always talk with a qualified healthcare professional
          about your individual situation. Dr. Weis Joint Research may receive
          compensation for referrals to third-party presentations.
        </p>
        <p className="mt-4 text-xs text-[#9AA79E] text-center" data-id="element-63" data-mp="1vhk4m71g0hqr">
          © {new Date().getFullYear()} Dr. Weis Joint Research. All rights
          reserved.
        </p>
      </footer>
    </div>;
}